/**
 * Featherweight loading facade for the iframe embed paths.
 *
 * Both `initialise()` and the `<TimumBooking>` component wrap the widget in an
 * iframe whose `srcdoc` re-imports booking.js from the CDN. Until that bundle
 * downloads and React mounts, the iframe used to show empty white space — the
 * slower the visitor's connection, the longer the void.
 *
 * These helpers inline a tiny, dependency-free loading spinner directly into the
 * srcdoc so *something* paints the instant the iframe mounts: no extra network
 * request, no React/MUI. The spinner deliberately mirrors the app's own
 * `ProgressComponent` (a centred ~40px circular spinner in #777 filling the
 * configured height) so the hand-off from facade → app is seamless — no fake
 * calendar structure that would mismatch whichever frontend actually renders.
 *
 * The spinner lives inside the React root element, so
 * `mountBooking()` → `createRoot(rootEl).render(...)` replaces it automatically
 * on first commit (React 18's `clearContainer` runs on the initial mount).
 *
 * NOTE: for the spinner to paint *before* the heavy bundle downloads, the code
 * that builds this srcdoc (initialise / init) must ship in a small entry chunk,
 * with the app graph behind a dynamic import — see src/mountBooking.jsx.
 */

// Shared ring primitives — the single source of truth for the loading spinner's
// geometry and colours, consumed by BOTH this facade CSS-string builder and the
// React <BookingSpinner> (src/components/base/BookingSpinner.jsx). Only the
// *rendering* differs (pre-React CSS string vs MUI sx); the numbers/colours live
// here so the facade → app spinner handoff can't drift.
export const RING = {
  color: '#777',
  trackLight: 'rgba(0,0,0,.12)',
  trackDark: 'rgba(255,255,255,.16)',
  duration: '.9s',
  defaultSize: 40,
};

/** Ring diameter + border width (px) for a given size; floors at 16px. */
export function ringMetrics(size) {
  const px = Math.max(16, Number.isFinite(size) ? size : RING.defaultSize);
  return { px, border: Math.max(2, Math.round(px * 0.09)) };
}

// Visually-hidden status text for the loader, in the 5 supported languages.
// The facade runs before React/i18next exist, so these few strings are inlined
// here and picked by `appConfig.culture` for screen-reader parity during load.
const LOADING_LABELS = {
  en: 'Loading available appointments…',
  de: 'Verfügbare Termine werden geladen…',
  fr: 'Chargement des rendez-vous disponibles…',
  it: 'Caricamento degli appuntamenti disponibili…',
  es: 'Cargando citas disponibles…',
};

/** Pick a loader label for a culture code (e.g. 'de', 'de-DE'); defaults to en. */
function labelForCulture(culture) {
  const key =
    typeof culture === 'string' ? culture.trim().slice(0, 2).toLowerCase() : '';
  return LOADING_LABELS[key] || LOADING_LABELS.en;
}

// Full-match colour allowlist. The value is interpolated raw into a `<style>`
// block, so we only accept shapes that cannot carry a `}` / `</style>` breakout:
// hex, rgb[a]/hsl[a] with numeric-only interiors, or a bare colour keyword.
const SAFE_COLOR =
  /^(#[0-9a-fA-F]{3,8}|(rgb|rgba|hsl|hsla)\([0-9.,%\s/]+\)|[a-zA-Z]+)$/;

/** Escape a value for safe use inside a double-quoted HTML attribute. */
function escapeHtmlAttr(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Escape a value for safe use as HTML text content. */
function escapeHtmlText(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Coerce an `appConfig.height` (number | numeric string | `'<n>px'`) into a CSS
 * length usable for `min-height`. Percentage / viewport heights can't drive a
 * `min-height` without a sized ancestor, so they fall back to the default box.
 *
 * Exported so the in-app `BookingSpinner` centres its ring at the same height as
 * this facade loader — the two must match for a seamless facade → app handoff.
 */
export function toCssHeight(height) {
  if (typeof height === 'number' && Number.isFinite(height)) return `${height}px`;
  if (typeof height === 'string') {
    const t = height.trim();
    if (/^\d+$/.test(t)) return `${t}px`;
    if (/^\d+px$/.test(t)) return t; // '1000px' — the documented string form
  }
  return '500px';
}

/**
 * Build the inline loading spinner markup (scoped `<style>` + a centred spinner).
 * Pure string builder — safe to embed inside an iframe `srcdoc`. Mirrors the
 * app's ProgressComponent so the facade → app transition is seamless.
 *
 * @param {object}        [opts]
 * @param {number|string} [opts.height]        widget height (`appConfig.height`)
 * @param {string}        [opts.color]         spinner colour (defaults to #777, matching the app)
 * @param {number}        [opts.size]          spinner diameter in px (defaults to 40, matching MUI)
 * @param {string}        [opts.culture]       culture code used to localize the a11y label
 * @param {string}        [opts.loadingLabel]  explicit visually-hidden status text (overrides culture)
 * @returns {string} HTML string
 */
export function buildBookingLoader({
  height,
  color,
  size,
  culture,
  loadingLabel,
} = {}) {
  const minHeight = toCssHeight(height);
  const spinnerColor =
    color && SAFE_COLOR.test(String(color).trim()) ? color : RING.color;
  const { px, border } = ringMetrics(size);
  const label = escapeHtmlText(loadingLabel || labelForCulture(culture));

  return `<style>
  .tmk-root{box-sizing:border-box;min-height:${minHeight};display:flex;align-items:center;justify-content:center;padding:8px}
  .tmk-spin{width:${px}px;height:${px}px;border-radius:50%;border:${border}px solid ${RING.trackLight};border-top-color:${spinnerColor};animation:tmk-rot ${RING.duration} linear infinite}
  .tmk-sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
  @keyframes tmk-rot{to{transform:rotate(360deg)}}
  @media (prefers-reduced-motion:reduce){.tmk-spin{animation:none;border-color:${spinnerColor};opacity:.5}}
  @media (prefers-color-scheme:dark){.tmk-spin{border-color:${RING.trackDark};border-top-color:${spinnerColor}}}
</style>
<div class="tmk-root" role="status">
  <div class="tmk-spin"></div>
  <span class="tmk-sr">${label}</span>
</div>`;
}

/**
 * Inject the loading spinner into `rootEl`, unless one is already present.
 * Idempotent by design: the iframe `srcdoc` paints the spinner before
 * booking-boot.js even downloads, so on that path this is a no-op (it must NOT
 * overwrite, which would restart the CSS animation); on the direct `init()` path
 * the root is empty and receives the spinner. Owning the `.tmk-spin` marker here
 * keeps the presence contract next to the markup that defines it.
 *
 * @param {HTMLElement} rootEl
 * @param {object} [opts] forwarded to `buildBookingLoader` (height, culture, …)
 */
export function ensureBookingLoader(rootEl, opts) {
  if (rootEl && !rootEl.querySelector('.tmk-spin')) {
    rootEl.innerHTML = buildBookingLoader(opts);
  }
}

/**
 * Build the iframe `srcdoc` that paints the loader immediately and then loads
 * booking.js from the CDN. The heavy import is wrapped in a load trigger:
 *
 *  - `'eager'`: import starts as soon as the iframe document runs.
 *  - `'idle'`: import is deferred until the HOST page has finished loading (its
 *    `load` event) and then to a browser-idle moment (`requestIdleCallback`, with
 *    a ~200 ms `setTimeout` fallback for Safari, and a safety cap). This keeps the
 *    heavy download from competing with the host page's own critical resources
 *    (LCP) for bandwidth/CPU — protecting the host's Core Web Vitals — with no
 *    visitor interaction. The loading spinner shows the whole time.
 *
 * Unknown modes (e.g. a future `'visible'`) degrade gracefully to eager.
 *
 * `initBody` is expected to `await init(...)`, so that a failure of *either* the
 * entry import or the app chunk it dynamically loads swaps the spinner for a
 * terminal error message instead of spinning forever.
 *
 * @param {object} opts
 * @param {string} opts.rootElId       id of the React root element inside the iframe (must be stable)
 * @param {string} opts.margin         CSS margin for the root wrapper
 * @param {string} opts.bootUrl        URL to import `init` from (the tiny booking-boot.js)
 * @param {string} opts.initBody       JS statements that `await init(...)` (has `init` in scope)
 * @param {string} [opts.loadMode]     'eager' | 'idle'
 * @param {string} [opts.loaderHtml]   inline loader markup
 * @returns {string} srcdoc HTML string
 */
export function buildBootstrapSrcdoc({
  rootElId,
  margin,
  bootUrl,
  initBody,
  loadMode = 'eager',
  loaderHtml = '',
}) {
  const safeRootElId = escapeHtmlAttr(rootElId);
  // JSON.stringify escapes quotes/backslashes but not `</script>`; neutralize
  // `<` so a stray value can't terminate the module script element.
  const jsBoot = JSON.stringify(bootUrl).replace(/</g, '\\u003c');
  const jsRootId = JSON.stringify(rootElId).replace(/</g, '\\u003c');

  const trigger =
    loadMode === 'idle'
      ? // Defer the heavy app-chunk download until the HOST page has finished
        // loading (its `load` event), so it never competes with the parent's
        // critical resources (LCP) for bandwidth/CPU — then wait for a browser-
        // idle moment. The srcdoc iframe is same-origin, so `window.parent`'s
        // load state is readable; a safety timeout guarantees the widget still
        // loads if the host never fires `load`, and any access error (defensive:
        // srcdoc is always same-origin) falls back to plain idle deferral.
        `(function () {
          var idle = function () {
            (window.requestIdleCallback || function (cb, o) {
              return setTimeout(cb, o && o.timeout ? Math.min(o.timeout, 200) : 200);
            })(function () { __timumStart(); }, { timeout: 2500 });
          };
          try {
            var p = window.parent;
            if (!p || p === window || p.document.readyState === 'complete') {
              idle();
            } else {
              var done = false;
              var go = function () { if (done) return; done = true; idle(); };
              p.addEventListener('load', go, { once: true });
              setTimeout(go, 10000);
            }
          } catch (e) { idle(); }
        })();`
      : `__timumStart();`;

  return `
  <div id="${safeRootElId}" style="margin: ${margin}">
    ${loaderHtml}
    <script type="module">
      const __timumStart = async () => {
        try {
          const { init } = await import(${jsBoot});
          ${initBody}
        } catch (err) {
          const __root = window.document.getElementById(${jsRootId});
          if (__root) {
            __root.innerHTML =
              '<div role="alert" style="padding:16px;font:14px system-ui,-apple-system,sans-serif;color:#555">'
              + 'Booking could not be loaded. Please refresh to try again.'
              + '</div>';
          }
          console.error('[timum] booking bundle failed to load', err);
        }
      };
      ${trigger}
    </script>
  </div>
  `;
}
